import {faExclamationTriangle} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {mthen, maybe} from '@freckle/maybe'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Container} from '@freckle/educator-materials/ts/components/container'
import {H2} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import RosterBody from '@freckle/school/ts/rostering/components/common/roster-body'
import {listGroup, exclamationIcon, fixCsvButtonWrapper} from './failed-import.module.scss'

export type FailedImportErrorT = {
  message: string
  metadata?: ValidationErrorMetadataT | null
}

type ValidationErrorMetadataT = {
  csvFileName?: string
  schoolIdpId?: number
  schoolName?: string
  courseId?: string
  studentId?: string
  idpId?: string
  rowNumber?: number | null
  otherRowNumber?: number | null
  studentSisId?: string | null
  schoolId?: string | null
  courseSisId?: string | null
  teacherEmail?: string | null
}

type Props = {
  errors: Array<FailedImportErrorT>
  onFixCsv: () => void
}

export default function FailedImport(props: Props) {
  const {errors, onFixCsv} = props
  const {t} = useSafeTranslation()

  return (
    <RosterBody>
      <Container>
        <H2>{t('IMPORT_ROSTER_RAILED_IMPORT_TITLE')}</H2>

        <ul className={listGroup}>
          <li>
            <FontAwesomeIcon className={exclamationIcon} icon={faExclamationTriangle} />
            <span>{t('IMPORT_ROSTER_RAILED_IMPORT_ERROR_COUNT', {numErrors: errors.length})}</span>
          </li>
          {errors.map((error, i: number) => (
            <FailedImportItem key={i} error={error} />
          ))}
        </ul>
        <br />
        <div className={fixCsvButtonWrapper}>
          <Button dataTest="rostering-fix-csv-and-upload-again" onClick={onFixCsv}>
            {t('IMPORT_ROSTER_RAILED_IMPORT_BUTTON_TRY_AGAIN')}
          </Button>
        </div>
      </Container>
    </RosterBody>
  )
}

function FailedImportItem({error}: {error: FailedImportErrorT}) {
  const {message, metadata} = error

  const csvFileName = mthen(metadata, metadata => metadata.csvFileName)
  const schoolName = mthen(metadata, metadata => metadata.schoolName)
  const courseId = mthen(metadata, metadata => metadata.courseId)
  const studentId = mthen(metadata, metadata => metadata.studentId)
  const idpId = mthen(metadata, metadata => metadata.idpId)
  const rowNumber = mthen(metadata, metadata => metadata.rowNumber)
  const otherRowNumber = mthen(metadata, metadata => metadata.otherRowNumber)
  const studentSisId = mthen(metadata, metadata => metadata.studentSisId)
  const schoolId = mthen(metadata, metadata => metadata.schoolId)
  const courseSisId = mthen(metadata, metadata => metadata.courseSisId)
  const teacherEmail = mthen(metadata, metadata => metadata.teacherEmail)

  const filenameTag = maybe(
    () => '',
    csvFileName => `[Filename: ${csvFileName}] `,
    csvFileName,
  )
  const schoolNameTag = maybe(
    () => '',
    schoolName => `[School: ${schoolName}] `,
    schoolName,
  )
  const courseIdTag = maybe(
    () => '',
    courseId => `[Course ID: ${courseId}] `,
    courseId,
  )
  const studentIdTag = maybe(
    () => '',
    studentId => `[Student ID: ${studentId}] `,
    studentId,
  )
  const idpIdTag = maybe(
    () => '',
    idpId => `[IDP ID: ${idpId}] `,
    idpId,
  )
  const studentSisIdTag = maybe(
    () => '',
    studentSisId => `[Student SIS ID #: ${studentSisId}] `,
    studentSisId,
  )
  const rowNumberTag = maybe(
    () => '',
    rowNumber => `[Row #: ${rowNumber}] `,
    rowNumber,
  )
  const otherRowNumberTag = maybe(
    () => '',
    otherRowNumber => `[Other Row #: ${otherRowNumber}] `,
    otherRowNumber,
  )
  const schoolIdTag = maybe(
    () => '',
    schoolId => `[School ID: ${schoolId}] `,
    schoolId,
  )
  const courseSisIdTag = maybe(
    () => '',
    courseSisId => `[Course SIS ID: ${courseSisId}] `,
    courseSisId,
  )
  const teacherEmailTag = maybe(
    () => '',
    teacherEmail => `[Teacher Email: ${teacherEmail}] `,
    teacherEmail,
  )

  return (
    <li>
      {message} {filenameTag}
      {schoolNameTag}
      {schoolIdTag}
      {courseIdTag}
      {courseSisIdTag}
      {teacherEmailTag}
      {studentIdTag}
      {idpIdTag}
      {studentSisIdTag}
      {rowNumberTag}
      {otherRowNumberTag}
    </li>
  )
}
